import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { Calendar, Pill, FileText, MessageCircle, LogOut, Users, Activity } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function DoctorDashboard() {
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState(location.state?.user || null);
  const [appointments, setAppointments] = useState([]);
  const [refills, setRefills] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [appointmentsRes, refillsRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/appointments`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/refills`, { withCredentials: true })
      ]);
      setAppointments(appointmentsRes.data);
      setRefills(refillsRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${BACKEND_URL}/api/auth/logout`, {}, { withCredentials: true });
      navigate('/login');
    } catch (error) {
      toast.error('Logout failed');
    }
  };

  const handleApproval = async (type, id, status) => {
    try {
      const endpoint = type === 'appointment' ? 'appointments' : 'refills';
      await axios.put(
        `${BACKEND_URL}/api/${endpoint}/${id}`,
        { status },
        { withCredentials: true }
      );
      toast.success(`${type === 'appointment' ? 'Appointment' : 'Refill'} ${status}`);
      loadData();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const pendingAppointments = appointments.filter(a => a.status === 'pending');
  const pendingRefills = refills.filter(r => r.status === 'pending');

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
          <p className="mt-4 text-text-secondary font-body">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="doctor-dashboard">
      {/* Header */}
      <div className="glass-nav sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Activity className="w-8 h-8 text-primary" />
              <div>
                <h1 className="font-heading text-xl font-semibold text-text-primary">Doctor Dashboard</h1>
                <p className="text-sm text-text-secondary font-body">{user?.name}</p>
              </div>
            </div>
            <button
              data-testid="logout-btn"
              onClick={handleLogout}
              className="flex items-center gap-2 text-text-secondary hover:text-text-primary transition-colors"
            >
              <LogOut className="w-5 h-5" />
              <span className="font-body">Logout</span>
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="card-default" data-testid="stats-appointments">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-text-secondary font-body text-sm">Pending Appointments</p>
                <p className="text-3xl font-heading font-semibold text-text-primary mt-2">{pendingAppointments.length}</p>
              </div>
              <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                <Calendar className="w-6 h-6 text-primary" />
              </div>
            </div>
          </div>

          <div className="card-default" data-testid="stats-refills">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-text-secondary font-body text-sm">Pending Refills</p>
                <p className="text-3xl font-heading font-semibold text-text-primary mt-2">{pendingRefills.length}</p>
              </div>
              <div className="w-12 h-12 bg-accent/10 rounded-lg flex items-center justify-center">
                <Pill className="w-6 h-6 text-accent" />
              </div>
            </div>
          </div>

          <div className="card-default">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-text-secondary font-body text-sm">Total Patients</p>
                <p className="text-3xl font-heading font-semibold text-text-primary mt-2">-</p>
              </div>
              <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                <Users className="w-6 h-6 text-primary" />
              </div>
            </div>
          </div>
        </div>

        {/* Navigation Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <button
            data-testid="nav-appointments-btn"
            onClick={() => navigate('/appointments')}
            className="card-default hover:shadow-soft transition-all text-left"
          >
            <Calendar className="w-8 h-8 text-primary mb-3" />
            <h3 className="font-heading text-lg font-semibold text-text-primary mb-1">Appointments</h3>
            <p className="text-text-secondary font-body text-sm">Manage appointment requests</p>
          </button>

          <button
            data-testid="nav-refills-btn"
            onClick={() => navigate('/refills')}
            className="card-default hover:shadow-soft transition-all text-left"
          >
            <Pill className="w-8 h-8 text-accent mb-3" />
            <h3 className="font-heading text-lg font-semibold text-text-primary mb-1">Refills</h3>
            <p className="text-text-secondary font-body text-sm">Review refill requests</p>
          </button>

          <button
            data-testid="nav-regimens-btn"
            onClick={() => navigate('/regimen')}
            className="card-default hover:shadow-soft transition-all text-left"
          >
            <FileText className="w-8 h-8 text-primary mb-3" />
            <h3 className="font-heading text-lg font-semibold text-text-primary mb-1">Regimens</h3>
            <p className="text-text-secondary font-body text-sm">Create treatment plans</p>
          </button>

          <button
            data-testid="nav-patients-btn"
            onClick={() => navigate('/patients')}
            className="card-default hover:shadow-soft transition-all text-left"
          >
            <Users className="w-8 h-8 text-accent mb-3" />
            <h3 className="font-heading text-lg font-semibold text-text-primary mb-1">Patients</h3>
            <p className="text-text-secondary font-body text-sm">View patient profiles</p>
          </button>
        </div>

        {/* Pending Requests */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Pending Appointments */}
          <div className="card-default" data-testid="pending-appointments-section">
            <h2 className="font-heading text-xl font-semibold text-text-primary mb-4">Pending Appointments</h2>
            {pendingAppointments.length === 0 ? (
              <p className="text-text-secondary font-body">No pending appointments</p>
            ) : (
              <div className="space-y-3">
                {pendingAppointments.slice(0, 5).map((appointment) => (
                  <div key={appointment.appointment_id} className="border border-stone-100 rounded-lg p-4" data-testid={`appointment-${appointment.appointment_id}`}>
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <p className="font-body font-medium text-text-primary">{appointment.reason}</p>
                        <p className="text-sm text-text-secondary font-body mt-1">
                          {appointment.requested_date} at {appointment.requested_time}
                        </p>
                      </div>
                    </div>
                    <div className="flex gap-2 mt-3">
                      <button
                        data-testid={`approve-appointment-${appointment.appointment_id}`}
                        onClick={() => handleApproval('appointment', appointment.appointment_id, 'approved')}
                        className="flex-1 bg-status-success text-white rounded-lg px-4 py-2 text-sm font-medium hover:opacity-90 transition-opacity"
                      >
                        Approve
                      </button>
                      <button
                        data-testid={`deny-appointment-${appointment.appointment_id}`}
                        onClick={() => handleApproval('appointment', appointment.appointment_id, 'denied')}
                        className="flex-1 bg-status-error text-white rounded-lg px-4 py-2 text-sm font-medium hover:opacity-90 transition-opacity"
                      >
                        Deny
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Pending Refills */}
          <div className="card-default" data-testid="pending-refills-section">
            <h2 className="font-heading text-xl font-semibold text-text-primary mb-4">Pending Refills</h2>
            {pendingRefills.length === 0 ? (
              <p className="text-text-secondary font-body">No pending refills</p>
            ) : (
              <div className="space-y-3">
                {pendingRefills.slice(0, 5).map((refill) => (
                  <div key={refill.refill_id} className="border border-stone-100 rounded-lg p-4" data-testid={`refill-${refill.refill_id}`}>
                    <div className="mb-2">
                      <p className="font-body font-medium text-text-primary">{refill.medication_name}</p>
                      <p className="text-sm text-text-secondary font-body mt-1">
                        {refill.dosage} - Qty: {refill.quantity}
                      </p>
                    </div>
                    <div className="flex gap-2 mt-3">
                      <button
                        data-testid={`approve-refill-${refill.refill_id}`}
                        onClick={() => handleApproval('refill', refill.refill_id, 'approved')}
                        className="flex-1 bg-status-success text-white rounded-lg px-4 py-2 text-sm font-medium hover:opacity-90 transition-opacity"
                      >
                        Approve
                      </button>
                      <button
                        data-testid={`deny-refill-${refill.refill_id}`}
                        onClick={() => handleApproval('refill', refill.refill_id, 'denied')}
                        className="flex-1 bg-status-error text-white rounded-lg px-4 py-2 text-sm font-medium hover:opacity-90 transition-opacity"
                      >
                        Deny
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}